import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { devLog } from "@/lib/log";
import { useApp } from "@/contexts/AppContext";
import { Briefcase, FolderOpen, ChevronRight, Receipt, AlertTriangle, FileText, ChevronDown } from "lucide-react";
import AttachmentPreview from "@/components/AttachmentPreview";
import CostBreakdownStrip from "@/components/CostBreakdownStrip";
import JobPickerSheet from "@/components/JobPickerSheet";

function money(n) {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function JobHomeWidget({ compact = false } = {}) {
  const { jobs, activeJobId } = useApp();
  const nav = useNavigate();
  const [spend, setSpend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(null);

  const job = jobs.find((j) => j.id === activeJobId);
  const atts = job?.attachments || [];
  // Only fire the per-job spend fetch once we've confirmed the activeJobId
  // belongs to the current user's freshly-loaded `jobs` list. Skipping this
  // gate causes a boot-time 401/404 storm when a stale activeJobId (persisted
  // in localStorage from a previous session/user) is used before AppContext
  // has hydrated — the request races the /jobs list load and hits the API
  // with a job the current user doesn't own.
  const jobsHydrated = jobs.length > 0;
  const jobExistsForUser = jobsHydrated && !!job;

  useEffect(() => {
    if (!activeJobId || !jobExistsForUser) {
      setSpend(null);
      return;
    }
    let mounted = true;
    setLoading(true);
    api
      .get(`/jobs/${activeJobId}/spend`)
      .then(({ data }) => {
        if (mounted) setSpend(data);
      })
      .catch((e) => {
        devLog.warn("job spend load failed", e);
        if (mounted) setSpend(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [activeJobId, jobExistsForUser]);

  if (jobs.length === 0) return null;
  if (!job) return null;

  const budget = spend?.budget || 0;
  const spent = spend?.spent || 0;
  const pct = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
  const overBudget = budget > 0 && spent > budget;
  const nearBudget = budget > 0 && pct >= 85 && !overBudget;
  const barColor = overBudget ? "#F87171" : nearBudget ? "#FBBF24" : "#34D399";
  const remaining = Math.max(0, budget - spent);

  return (
    <>
      <section
        className={`bi-surface border border-white/5 ${compact ? "p-3" : "p-4 space-y-3"}`}
        data-testid="job-home-widget"
      >
        {/* Header row + job picker — hidden in compact mode because the
            global TopBar already shows the JOB chip. Repeating it here on
            Capture only clutters the last tile with a second dropdown. */}
        {!compact && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-white/50 text-[10px] uppercase tracking-[0.2em] font-heading">
              <Briefcase className="w-3 h-3" /> Active Job
            </div>
            {jobs.length > 1 && (
              <JobPickerSheet testIdPrefix="job-home">
                <button
                  type="button"
                  data-testid="job-home-selector"
                  aria-label="Switch active job"
                  className="inline-flex items-center gap-1.5 h-8 max-w-[60%] rounded-lg bi-surface hover:brightness-110 active:scale-95 transition px-2.5 pr-1.5 text-xs font-medium text-white/90"
                >
                  <span className="truncate">{job?.name || "Select job"}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-white/60 shrink-0" />
                </button>
              </JobPickerSheet>
            )}
          </div>
        )}

        {compact ? (
          // Compact — one tight row for Capture: title/client + spend/budget
          // on a single baseline, thin bar underneath, Open link at the right.
          // Skips CostBreakdownStrip + Site Docs (both stay on /jobs).
          <div>
            <div className="flex items-baseline justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-heading font-semibold text-[17px] leading-tight truncate">
                  {job.name}
                </div>
                {job.client && (
                  <div className="text-[11px] text-white/50 truncate">
                    {job.client}
                  </div>
                )}
              </div>
              {budget > 0 ? (
                <div
                  className="text-right shrink-0 tabular-nums leading-tight"
                  data-testid="job-home-spent"
                >
                  <span className="font-heading font-extrabold text-[15px] text-white">
                    {money(spent)}
                  </span>
                  <span
                    className="text-[11px] text-white/50 ml-1"
                    data-testid="job-home-budget"
                  >
                    / {money(budget)}
                  </span>
                </div>
              ) : (
                spend && (
                  <div className="text-[11px] text-white/50 shrink-0 tabular-nums">
                    {money(spent)}
                  </div>
                )
              )}
              <button
                type="button"
                data-testid="job-home-open"
                onClick={() => nav("/jobs")}
                className="text-[#FF9500] text-xs inline-flex items-center gap-0.5 hover:underline shrink-0"
              >
                Open <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            {budget > 0 && (
              <div
                className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden"
                data-testid="job-home-bar-wrap"
              >
                <div
                  className="h-full transition-[width] duration-500"
                  style={{ width: `${pct}%`, background: barColor }}
                  data-testid="job-home-bar"
                />
              </div>
            )}
          </div>
        ) : (
        <div>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-heading font-extrabold text-lg leading-tight truncate">
                {job.name}
              </div>
              {job.client && (
                <div className="text-[11px] text-white/50 truncate">{job.client}</div>
              )}
            </div>
            <button
              type="button"
              data-testid="job-home-open"
              onClick={() => nav("/jobs")}
              className="text-[#FF9500] text-xs inline-flex items-center gap-0.5 hover:underline shrink-0 mt-0.5"
            >
              Open <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {budget > 0 ? (
            <div className="mt-2">
              <div className="flex items-baseline justify-between">
                <div
                  className="text-2xl font-heading font-extrabold tabular-nums"
                  data-testid="job-home-spent"
                >
                  {money(spent)}
                </div>
                <div className="text-xs text-white/50" data-testid="job-home-budget">
                  of {money(budget)}
                </div>
              </div>
              <div className="mt-1.5 h-2 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full transition-[width] duration-500"
                  style={{ width: `${pct}%`, background: barColor }}
                  data-testid="job-home-bar"
                />
              </div>
              <div
                className="mt-1 text-[11px] inline-flex items-center gap-1"
                style={{ color: barColor }}
                data-testid="job-home-status"
              >
                {overBudget ? (
                  <>
                    <AlertTriangle className="w-3 h-3" />
                    {money(spent - budget)} over budget
                  </>
                ) : (
                  <>{money(remaining)} left · {Math.round(100 - pct)}% remaining</>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-2 text-sm text-white/70" data-testid="job-home-nobudget">
              {loading ? (
                "Loading…"
              ) : spend ? (
                <>
                  Logged <b className="text-white">{money(spent)}</b> across{" "}
                  <b className="text-white">{spend.entry_count}</b>{" "}
                  {spend.entry_count === 1 ? "entry" : "entries"} · No budget set
                </>
              ) : (
                "—"
              )}
            </div>
          )}
        </div>
        )}

        {/* GST / breakdown strip / Site Docs — full-widget mode only. On
            Capture these live on /jobs; we don't repeat them here. */}
        {!compact && spend && spend.gst > 0 && (
          <div className="text-[11px] text-[#34D399] inline-flex items-center gap-1.5">
            <Receipt className="w-3.5 h-3.5" /> GST identified:{" "}
            <b>{money(spend.gst)}</b>
          </div>
        )}

        {!compact && <CostBreakdownStrip jobId={activeJobId} />}

        {!compact && atts.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5 inline-flex items-center gap-1">
              <FolderOpen className="w-3 h-3" /> Site docs · {atts.length}
            </div>
            <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1">
              {atts.slice(0, 8).map((a, idx) => (
                <button
                  key={a.id}
                  type="button"
                  data-testid={`job-home-doc-${a.id}`}
                  onClick={() => setPreviewIdx(idx)}
                  className="w-14 h-14 rounded-lg bi-surface-2 overflow-hidden shrink-0 hover:brightness-110 active:scale-95 transition"
                  title={a.filename}
                >
                  {a.kind === "photo" ? (
                    <img
                      src={`${API_BASE}/api/files/${a.path}`}
                      alt={a.filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[#FF9500] p-1 gap-0.5">
                      <FileText className="w-4 h-4" />
                      <span className="text-[8px] text-white/60 truncate max-w-full leading-none">
                        {a.filename}
                      </span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
      {previewIdx !== null && (
        <AttachmentPreview
          items={atts}
          startIndex={previewIdx}
          onClose={() => setPreviewIdx(null)}
        />
      )}
    </>
  );
}
